/* eslint-disable */
import { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
    AppBar, Box, CssBaseline, Toolbar, IconButton,
    Drawer, List, ListItemButton, ListItemIcon, ListItemText,
    Divider, Typography, useMediaQuery, Avatar, Badge
} from '@mui/material';
import {
    IconMenu2, IconLayoutDashboard, IconBriefcase, IconLogout,
    IconUserCircle, IconChevronLeft
} from '@tabler/icons';
import LogoSection from 'layout/MainLayout/LogoSection';
import { auth, toastifySuccess } from 'helper/Utility';
import { useLocation } from 'react-router-dom';

export const SP_DRAWER_WIDTH = 260;

export default function SalespersonLayout() {
    const theme = useTheme();
    const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));
    const [drawerOpen, setDrawerOpen] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const authData = auth('salesperson');

    const handleDrawerToggle = () => setDrawerOpen((p) => !p);

    const handleLogout = () => {
        sessionStorage.removeItem('salesperson-secret');
        toastifySuccess('Logged out successfully');
        navigate('/admin/login', { replace: true });
    };

    const navItems = [
        {
            label: 'Dashboard',
            icon: <IconLayoutDashboard stroke={1.5} size="1.2rem" />,
            path: '/salesperson/dashboard'
        },
    ];

    const drawerContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#ffffff' }}>
            {/* Logo Section */}
            <Box sx={{
                px: 2.5,
                py: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minHeight: 64,
                borderBottom: '1px solid #f0f0f0'
            }}>
                <LogoSection />
                {!matchUpMd && (
                    <IconButton onClick={handleDrawerToggle} size="small">
                        <IconChevronLeft size="1rem" />
                    </IconButton>
                )}
            </Box>

            {/* User Profile Section */}
            <Box sx={{
                p: 2.5,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                bgcolor: '#fafafa',
                borderBottom: '1px solid #f0f0f0'
            }}>
                <Avatar
                    sx={{
                        bgcolor: '#41a0ff',
                        width: 44,
                        height: 44,
                        boxShadow: '0 2px 8px rgba(6, 41, 76, 0.2)'
                    }}
                >
                    {authData?.firstName?.[0]?.toUpperCase() || 'S'}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                        {authData?.firstName} {authData?.lastName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Badge color="success" variant="dot" sx={{ '& .MuiBadge-badge': { width: 8, height: 8, borderRadius: '50%' } }} />
                        Salesperson
                    </Typography>
                </Box>
            </Box>

            {/* Navigation */}
            <List sx={{ px: 1.5, py: 2, flexGrow: 1 }}>
                {navItems.map((item) => {
                    const active = location.pathname.startsWith(item.path);
                    return (
                        <ListItemButton
                            key={item.path}
                            onClick={() => {
                                navigate(item.path);

                                if (!matchUpMd) {
                                    setDrawerOpen(false);
                                }
                            }}
                            selected={active}
                            sx={{
                                borderRadius: '10px',
                                mb: 0.5,
                                py: 1,
                                px: 1.5,
                                transition: 'all 0.2s ease',
                                '&.Mui-selected': {
                                    bgcolor: '#1976d2',
                                    color: '#fff',

                                    '& .MuiListItemIcon-root': {
                                        color: '#fff'
                                    },

                                    '& .MuiTypography-root': {
                                        color: '#fff'
                                    },

                                    '&:hover': {
                                        bgcolor: '#1565c0'
                                    }
                                },
                                '&:hover': {
                                    bgcolor: '#f5f5f5',
                                    transform: 'translateX(4px)'
                                },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 36, color: active ? '#1976d2' : '#666' }}>
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{
                                    fontSize: 14,
                                    fontWeight: active ? 600 : 500,
                                    sx: { color: active ? '#1976d2' : '#333' }
                                }}
                            />
                        </ListItemButton>
                    );
                })}
            </List>

            <Divider sx={{ my: 1 }} />

            {/* Logout Button */}
            <List sx={{ px: 1.5, pb: 2.5 }}>
                    {/* <ListItemButton
                        onClick={handleLogout}
                        sx={{
                            borderRadius: '10px',
                            py: 1,
                            px: 1.5,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                bgcolor: '#ffebee',
                                transform: 'translateX(4px)'
                            }
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 36, color: '#d32f2f' }}>
                            <IconLogout stroke={1.5} size="1.2rem" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Logout"
                            primaryTypographyProps={{
                                fontSize: 14,
                                fontWeight: 500,
                                sx: { color: '#d32f2f' }
                            }}
                        />
                    </ListItemButton> */}
            </List>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f7fb' }}>
            <CssBaseline />

            {/* HEADER */}
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    bgcolor: '#fff',
                    color: '#333',
                    zIndex: theme.zIndex.drawer + 1,
                    borderBottom: '1px solid #e8e8e8',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                    width: matchUpMd && drawerOpen
                        ? `calc(100% - ${SP_DRAWER_WIDTH}px)`
                        : '100%',
                    ml: matchUpMd && drawerOpen
                        ? `${SP_DRAWER_WIDTH}px`
                        : 0,
                    transition: 'all 0.2s ease'
                }}
            >
                <Toolbar sx={{ minHeight: '64px !important', px: 2 }}>
                    <IconButton
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{
                            mr: 2,
                            color: '#666',
                            '&:hover': { bgcolor: '#f5f5f5' }
                        }}
                    >
                        <IconMenu2 stroke={1.5} size="1.3rem" />
                    </IconButton>

                    <Typography
                        variant="h4"
                        fontWeight={700}
                        sx={{
                            flexGrow: 1,
                            color: '#1976d2'
                        }}
                    >
                        Salesperson Dashboard
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                            <Typography variant="body2" fontWeight={600}>
                                {authData?.firstName} {authData?.lastName}
                            </Typography>

                            <Typography variant="caption" color="text.secondary">
                                Salesperson
                            </Typography>
                        </Box>

                        <Avatar
                            sx={{
                                width: 38,
                                height: 38,
                                bgcolor: '#43a1ff'
                            }}
                        >
                            {authData?.firstName?.[0] || 'S'}
                        </Avatar>

                        <IconButton
                            onClick={handleLogout}
                            sx={{
                                color: '#d32f2f',
                                '&:hover': {
                                    bgcolor: '#ffebee'
                                }
                            }}
                        >
                            <IconLogout stroke={1.5} size="1.1rem" />
                        </IconButton>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* SIDEBAR */}
            <Drawer
                variant={matchUpMd ? 'persistent' : 'temporary'}
                open={drawerOpen}
                onClose={handleDrawerToggle}
                sx={{
                    width: drawerOpen ? SP_DRAWER_WIDTH : 0,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: SP_DRAWER_WIDTH,
                        boxSizing: 'border-box',
                        borderRight: '1px solid #e8e8e8',
                        bgcolor: '#fff',
                        boxShadow: '2px 0 10px rgba(0,0,0,0.03)'
                    }
                }}
            >
                {drawerContent}
            </Drawer>

            {/* MAIN CONTENT */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    pt: '80px',
                    px: 2,
                    pb: 2,
                    transition: 'all 0.2s ease'
                }}
            >
                <Outlet />
            </Box>
        </Box>
    );
}